<template>
  <div class="login-bg align-center relative flex !h-screen items-center justify-center">
    <div class="absolute left-4 top-4">
      <ThemeSwitcher v-model="isDark" />
    </div>
    <div
      class="login-card h-fit w-11/12 rounded-xl p-8 shadow backdrop-blur-md sm:w-10/12 md:w-[420px] md:p-12"
    >
      <div class="mb-4 flex items-center justify-center gap-4">
        <img src="@/assets/open-mdx.png" alt="logo openmdx" class="h-18 w-auto" />
      </div>

      <div class="mt-8 mb-4 flex rounded-lg overflow-hidden border border-sky-200">
        <button
          class="flex-1 py-2 text-sm font-semibold transition-colors"
          :class="activeTab === 'email' ? 'bg-sky-600 text-white' : 'bg-white/50 text-sky-800 hover:bg-sky-50'"
          @click="switchTab('email')"
        >
          {{ languageStore.t('auth.login.tabEmail') }}
        </button>
        <button
          class="flex-1 py-2 text-sm font-semibold transition-colors"
          :class="activeTab === 'ldap' ? 'bg-sky-600 text-white' : 'bg-white/50 text-sky-800 hover:bg-sky-50'"
          @click="switchTab('ldap')"
        >
          {{ languageStore.t('auth.login.tabLdap') }}
        </button>
      </div>

      <div v-if="activeTab === 'email'" class="text-sky-900">
        <label class="mb-2 block font-bold">{{ languageStore.t('auth.login.emailLabel') }}</label>
        <InputText
          id="email"
          type="email"
          :placeholder="languageStore.t('auth.login.placeholders.email')"
          @keydown.enter="login"
          v-model="emailForm.email"
          :class="{ 'p-invalid': errors.email }"
          class="w-full"
        />
        <small v-if="errors.email" class="text-red-500">{{ errors.email }}</small>
        <label class="mt-4 block font-bold">{{ languageStore.t('auth.login.passwordLabel') }}</label>
        <div class="relative w-full">
          <InputText
            id="password"
            :type="showPassword"
            :placeholder="languageStore.t('auth.login.placeholders.password')"
            :class="{ 'p-invalid': errors.password }"
            class="w-full pr-10"
            v-model="emailForm.password"
            @keydown.enter="login"
          />
          <Icons
            :icon="passwordIcon"
            :size="24"
            class="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400"
            @click="handlePassword"
          />
        </div>
        <small v-if="errors.password" class="text-red-500">{{ errors.password }}</small>
        <div class="my-4">
          <RecoverPassword />
        </div>
        <Button
          :label="languageStore.t('auth.login.submitButton')"
          class="w-full"
          @click="login"
          :loading="isLoading"
        />
      </div>

      <div v-if="activeTab === 'ldap'" class="text-sky-900">
        <label class="mb-2 block font-bold">{{ languageStore.t('auth.login.usernameLdapLabel') }}</label>
        <InputText
          id="ldapUsername"
          type="text"
          :placeholder="languageStore.t('auth.login.usernameLdapPlaceholder')"
          @keydown.enter="loginAd"
          v-model="ldapForm.username"
          :class="{ 'p-invalid': errors.ldapUsername }"
          class="w-full"
        />
        <small v-if="errors.ldapUsername" class="text-red-500">{{ errors.ldapUsername }}</small>
        <label class="mt-4 block font-bold">{{ languageStore.t('auth.login.passwordLabel') }}</label>
        <div class="relative w-full">
          <InputText
            id="ldapPassword"
            :type="showPassword"
            :placeholder="languageStore.t('auth.login.placeholders.password')"
            class="w-full pr-10"
            v-model="ldapForm.password"
            @keydown.enter="loginAd"
          />
          <Icons
            :icon="passwordIcon"
            :size="24"
            class="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400"
            @click="handlePassword"
          />
        </div>
        <div class="my-4" />
        <Button
          :label="languageStore.t('auth.login.submitButtonAd')"
          class="w-full"
          @click="loginAd"
          :loading="isLoading"
        />
      </div>


      <Tag severity="danger" :value="errorMessage" v-if="errorMessage" class="mt-4 w-full" />
      <div class="mt-4 flex flex-col items-center justify-center gap-4">
        <img src="@/assets/logo-nepen.png" alt="logo nepen" class="h-8 w-auto" />
        <SelectLanguage class="mx-2" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import ThemeSwitcher from '@/components/ThemeSwitcher .vue'
import RecoverPassword from '@/components/login/RecoverPassword.vue'
import Icons from '@/components/subcomponents/Icons.vue'
import SelectLanguage from '@/components/subcomponents/SelectLanguage.vue'
import { resetUnauthenticatedState, resetNoValidClientToast } from '@/composables/unauthenticatedToast'
import { useDarkMode } from '@/composables/useDarkMode'
import * as loginServices from '@/services/userService'
import { useClientStore } from '@/stores/ClientStore'
import { useLanguageStore } from '@/stores/LanguageStore'
import { useLoginValidator } from '@/validators/loginValidator'
import { useToast } from 'primevue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Tag from 'primevue/tag'
import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

const languageStore = useLanguageStore()
const toast = useToast()
const router = useRouter()
const { isDark } = useDarkMode()
const clientStore = useClientStore()

const { createForm, createEmptyErrors, normalizeCredential, validateAdForm, validateLocalForm } =
  useLoginValidator()

const emailForm = reactive(createForm())
const ldapForm = reactive({ username: '', password: '' })
const errors = reactive<{ email: string; password: string; ldapUsername: string }>({ email: '', password: '', ldapUsername: '' })
const isLoading = ref(false)
const showPassword = ref('password')
const passwordIcon = ref('eye-closed')
const errorMessage = ref('')
const activeTab = ref<'email' | 'ldap'>('email')

const switchTab = (tab: 'email' | 'ldap') => {
  activeTab.value = tab
  Object.assign(errors, { email: '', password: '', ldapUsername: '' })
  errorMessage.value = ''
  ldapForm.username = ''
  ldapForm.password = ''
  emailForm.email = ''
  emailForm.password = ''
}

const handlePassword = () => {
  showPassword.value = showPassword.value === 'password' ? 'text' : 'password'
  passwordIcon.value = passwordIcon.value === 'eye-closed' ? 'eye-open' : 'eye-closed'
}

const login = () => {
  const validation = validateLocalForm(emailForm)
  Object.assign(errors, validation.errors)
  if (!validation.isValid) {
    return
  }

  const normalizedEmail = normalizeCredential(emailForm.email)

  isLoading.value = true
  errorMessage.value = ''

  loginServices
    .login(normalizedEmail, emailForm.password)
    .then((res) => {
      resetUnauthenticatedState()
      resetNoValidClientToast()
      clientStore.resetState()
      const access = loginServices.auth(router, res.data.accessToken, res.data.user)
      if (!access) {
        errorMessage.value = 'Usuário não tem acesso a esta aplicação'
      }
    })
    .catch((error) => {
      errorMessage.value = error?.response?.data?.detail
      toast.add({
        summary: 'Erro',
        detail: errorMessage.value || 'Erro ao fazer login',
        severity: 'error',
        life: 3000,
      })
    })
    .finally(() => {
      isLoading.value = false
    })
}

const loginAd = () => {
  if (!ldapForm.username) {
    errors.ldapUsername = 'Informe o nome de usuário'
    return
  }

  const normalizedUsername = normalizeCredential(ldapForm.username)

  isLoading.value = true
  errorMessage.value = ''

  loginServices
    .loginAd(normalizedUsername, ldapForm.password, null)
    .then((res) => {
      resetUnauthenticatedState()
      resetNoValidClientToast()
      clientStore.resetState()
      const access = loginServices.auth(router, res.data.accessToken, res.data.user)
      if (!access) {
        errorMessage.value = 'Usuário não tem acesso a esta aplicação'
      }
    })
    .catch((error) => {
      errorMessage.value = error?.response?.data?.detail
      if (errorMessage.value === 'UserNotAssociated') {
        router.push({
          name: 'Associate',
          state: { username: ldapForm.username, password: ldapForm.password },
        })
        return
      }

      toast.add({
        summary: 'Erro',
        detail: errorMessage.value || 'Erro ao fazer login',
        severity: 'error',
        life: 3000,
      })
    })
    .finally(() => {
      isLoading.value = false
    })
}
</script>
